import type { CSSProperties } from "react";
import { pewterEdge } from "../../../shared/ui/pewter-edge";

/**
 * The outer radius of a stay page panel, matching the `rounded-[28px]` these
 * surfaces wore before they were framed.
 */
const PANEL_RADIUS = 28;

/**
 * The panel's own surface: a very slight diagonal so the white is not flat
 * under the band. Two near-whites, not a visible gradient.
 *
 * NO `padding-box` ON THE END. The panel lab wrote this value into the
 * `background` shorthand, where a box keyword is legal; `pewterEdge` puts it
 * in `background-image`, where it is not, and one invalid keyword makes the
 * browser drop the whole declaration. The band still drew, so the panel went
 * transparent with a frame around it and looked deliberate. The helper sets
 * `background-clip` itself.
 */
export const PANEL_FILL = "linear-gradient(135deg, #FCFCFE, #F8F8FB 45%, #FBFBFD)";

/**
 * Lift, kept close to what these panels already had. The band supplies the
 * edge, so the shadow only has to hold the card off the page rather than draw
 * a line around it.
 */
export const PANEL_SHADOW = [
  "0 0 0 1px rgba(17,14,57,0.04)",
  "0 8px 30px rgba(17,14,57,0.05)",
  "inset 0 0 0 1px rgba(17,14,57,0.07)",
].join(", ");

/**
 * The stay page's reading surface: a wide, pale pewter band around white.
 *
 * This is the `matte` variant of the house edge, which is the softer of the
 * two materials: a mounted print's board, lighter than the thing it surrounds.
 * The other one, the narrow `pewter` hairline, is for image cards and would
 * draw a dark ring at this width.
 *
 * It is the SHARED helper rather than the hex values the panel lab tried out,
 * deliberately. The lab's variants were five hand-written gradients each; the
 * helper already encodes the same recipe, tones itself to the surface it is
 * given, and is what the footer and the booking controls wear — so a later
 * change to the house edge reaches this page too instead of leaving it behind
 * as a copy nobody remembers to update.
 *
 * Applied to the two PANELS, not to the room cards inside them. Nesting one
 * framed surface in another gives two competing edges a few pixels apart, and
 * the card is already separated from the panel by its own hairline.
 */
export function softPewterPanel(): CSSProperties {
  return {
    ...pewterEdge(PANEL_FILL, PANEL_RADIUS, { variant: "matte" }),
    boxShadow: PANEL_SHADOW,
  };
}
